import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { useTheme } from '../contexts/ThemeContext';

import GameCard from '../components/GameCard';
import GameListItem from '../components/GameListItem';
import FilterPanel from '../components/FilterPanel';
import RandomPicker from '../components/RandomPicker';
import GameEditModal from '../components/GameEditModal';
// Keep Settings lazy loaded (less frequently used)
const SettingsModal = lazy(() => import('../components/SettingsModal'));
import gameService from '../services/gameService';
import bggService from '../services/bggService';

import { Plus, Search, Grid3X3, List, Dice6, Star, Trophy, Settings, Bookmark, Target, BookOpen, Download, RefreshCw, Edit3, Heart, Trash2 } from 'lucide-react';

const GamePicker = () => {
  const [games, setGames] = useState([]);
  const [filters, setFilters] = useState({});
  const [viewMode, setViewMode] = useState(() => {
    // More robust mobile detection - default to list view on mobile devices
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    return isMobile ? 'list' : 'grid';
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showRandomPicker, setShowRandomPicker] = useState(false);
  const [showAddGame, setShowAddGame] = useState(false);
  const [showEditGame, setShowEditGame] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [isRandomizing, setIsRandomizing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, owned: 0, wishlist: 0, try: 0 });
  const [isEnrichingBGG, setIsEnrichingBGG] = useState(false);
  const [bggProgress, setBggProgress] = useState({ current: 0, total: 0, gameName: '' });
  const [showSettings, setShowSettings] = useState(false);
  const [bggUrl, setBggUrl] = useState('');
  const [isLoadingBGG, setIsLoadingBGG] = useState(false);
  const [hasPinSet, setHasPinSet] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [showGameDetail, setShowGameDetail] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [gamesPerPage] = useState(30); // Show 30 games per page
  
  const { toast } = useToast();
  const { isDark } = useTheme();

  // New game form state
  const [newGame, setNewGame] = useState({
    name: '', minPlayers: 1, maxPlayers: 4, playtime: 30, complexity: 1,
    isCoOp: false, tags: '', description: '', owned: true, wishlist: false,
    try: false, weight: 2, favorite: false, image: 'placeholder-dice-logo',
    rating: null, notes: '', bggId: ''
  });

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load games from API on component mount
  useEffect(() => {
    loadGames();
    loadStats();
    checkAuthentication();
  }, []);

  // Filter games when filters or debounced search change
  useEffect(() => {
    loadGames();
  }, [filters, debouncedSearchTerm]);

  // Check authentication status
  const checkAuthentication = async () => {
    try {
      // Load PIN status from backend
      const pinResponse = await gameService.getPinStatus();
      setHasPinSet(pinResponse.has_pin);
      
      if (!pinResponse.has_pin) {
        setIsAuthenticated(true); // No PIN set, always authenticated
        return;
      }
      
      // Check session authentication
      const authToken = sessionStorage.getItem('gamemanager_auth_token');
      const authTimestamp = sessionStorage.getItem('gamemanager_auth_timestamp');
      
      if (authToken && authTimestamp) {
        const now = Date.now();
        const authTime = parseInt(authTimestamp);
        const isValid = now - authTime < 30 * 60 * 1000; // 30 minutes
        
        if (isValid) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error checking authentication:', error);
      setHasPinSet(false);
      setIsAuthenticated(true); // Default to authenticated on error
    }
  };

  const handleAuthChange = (authenticated) => {
    setIsAuthenticated(authenticated);
  };

  const handleGameClick = (game) => {
    setSelectedGame(game);
    setShowGameDetail(true);
  };

  const handleFetchBGGDataForNewGame = async () => {
    // Check authentication before allowing BGG fetch
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please enter PIN to access BoardGameGeek data fetching",
        variant: "destructive",
      });
      return;
    }

    if (!bggUrl.trim()) {
      toast({
        title: "BGG URL Required",
        description: "Please enter a BoardGameGeek URL or game ID",
        variant: "destructive",
      });
      return;
    }

    const bggId = extractBGGId(bggUrl);
    if (!bggId) {
      toast({
        title: "Invalid BGG URL",
        description: "Please enter a valid BoardGameGeek URL or game ID",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingBGG(true);
    
    try {
      console.log('Fetching BGG data for new game, ID:', bggId);
      
      const bggService = await import('../services/bggService');
      const bggData = await bggService.default.getGameDetails(bggId);
      
      if (!bggData) {
        toast({
          title: "BGG Data Not Found",
          description: "Could not find game data on BoardGameGeek. Please check the ID/URL and try again.",
          variant: "destructive",
        });
        return;
      }

      console.log('BGG data received for new game:', bggData);

      // Update new game form with BGG data
      setNewGame(prev => ({
        ...prev,
        name: bggData.name || prev.name,
        description: bggData.description || prev.description,
        image: bggData.image || prev.image,
        yearPublished: bggData.yearPublished || prev.yearPublished,
        minPlayers: bggData.players?.min || prev.minPlayers,
        maxPlayers: bggData.players?.max || prev.maxPlayers,
        playtime: bggData.playtime || prev.playtime,
        complexity: bggData.complexity || prev.complexity,
        tags: bggData.tags ? bggData.tags.join(', ') : prev.tags,
        bggId: bggId, // Set the BGG ID
      }));

      toast({
        title: "BGG Data Loaded!",
        description: `Successfully loaded data for "${bggData.name}". You can still modify any fields before adding.`,
      });
      
    } catch (error) {
      console.error('Error fetching BGG data for new game:', error);
      toast({
        title: "BGG Fetch Failed",
        description: "Could not fetch data from BoardGameGeek. Please try again or add the game manually.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingBGG(false);
    }
  };

  const handleStatsFilter = (filterType) => {
    // Clear other exclusive filters and set the selected one
    const newFilters = {
      ...filters,
      ownedOnly: filterType === 'owned',
      wishlistOnly: filterType === 'wishlist',
      tryOnly: filterType === 'try'
    };
    setFilters(newFilters);
    
    toast({
      title: `Filter Applied`,
      description: `Showing ${filterType} games only`,
    });
  };

  const requireAuthentication = (action) => {
    // If no PIN is set, allow action immediately
    if (!hasPinSet) {
      action();
      return;
    }
    
    // If already authenticated, allow action
    if (isAuthenticated) {
      action();
      return;
    }
    
    // If PIN is set but not authenticated, redirect to Settings with a helpful toast
    toast({
      title: "Authentication Required",
      description: "Opening settings to unlock with your PIN...",
    });
    
    // Small delay to show toast, then open settings
    setTimeout(() => {
      setShowSettings(true);
    }, 500);
  };

  const loadGames = async () => {
    try {
      setIsLoading(true);
      const filterParams = { ...filters };
      if (debouncedSearchTerm) {
        filterParams.search = debouncedSearchTerm;
      }
      
      const response = await gameService.getGames(filterParams);
      setGames(response.games);
    } catch (error) {
      toast({
        title: "Error loading games",
        description: "Failed to load games from server",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await gameService.getStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Memoize filtered games (though filtering is done server-side, this prevents unnecessary re-renders)
  const filteredGames = useMemo(() => games, [games]);
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredGames.length / gamesPerPage);
  const startIndex = (currentPage - 1) * gamesPerPage;
  const endIndex = startIndex + gamesPerPage;
  const paginatedGames = filteredGames.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, debouncedSearchTerm]);

  // Batch filter changes to reduce re-renders
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const handleClearFilters = () => {
    setFilters({});
    setSearchTerm('');
    toast({
      title: "Filters cleared",
      description: "All filters have been reset.",
    });
  };

  const handleResetHistory = () => {
    // In a full app, this would reset play history
    toast({
      title: "History reset",
      description: "Play history has been cleared.",
    });
  };

  const handlePickRandom = useCallback(() => {
    if (filteredGames.length === 0) {
      toast({
        title: "No games available",
        description: "Adjust your filters to include more games.",
        variant: "destructive",
      });
      return;
    }
    // PERFORMANCE: Open modal immediately, no delay
    setShowRandomPicker(true);
  }, [filteredGames.length, toast]);

  const handleGameEdit = useCallback((game) => {
    // Inline authentication check to avoid closure issues
    // If no PIN is set, allow action immediately
    if (!hasPinSet) {
      setEditingGame(game);
      setShowEditGame(true);
      return;
    }
    
    // If already authenticated, allow action
    if (isAuthenticated) {
      setEditingGame(game);
      setShowEditGame(true);
      return;
    }
    
    // If PIN is set but not authenticated, redirect to Settings
    toast({
      title: "Authentication Required",
      description: "Opening settings to unlock with your PIN...",
    });
    
    setTimeout(() => {
      setShowSettings(true);
    }, 500);
  }, [isAuthenticated, hasPinSet, toast]);

  const handleSaveGame = useCallback(async (updatedGame) => {
    try {
      await gameService.updateGame(updatedGame.id, updatedGame);
      loadGames();
      loadStats();
    } catch (error) {
      throw error; // Re-throw to let the modal handle the error display
    }
  }, []);

  const handleDeleteGame = useCallback(async (gameId) => {
    try {
      await gameService.deleteGame(gameId);
      loadGames();
      loadStats();
    } catch (error) {
      throw error; // Re-throw to let the modal handle the error display
    }
  }, []);

  const handlePlayGame = async (game) => {
    try {
      await gameService.updateGameStatus(game.id, {
        lastPlayed: new Date().toISOString(),
      });
      
      toast({
        title: "Game session recorded!",
        description: `Recorded play session for ${game.name}`,
      });
      
      loadGames();
    } catch (error) {
      toast({
        title: "Error recording session",
        description: "Failed to record play session",
        variant: "destructive",
      });
    }
  };

  const handleToggleOwned = useCallback(async (gameId) => {
    try {
      const game = games.find(g => g.id === gameId);
      
      // New logic: if game is "want to try", clicking wishlist icon changes it to wishlist
      if (game.try) {
        await gameService.updateGameStatus(gameId, { 
          wishlist: true, 
          owned: false, 
          try: false 
        });
        
        // Optimistic update
        setGames(prev => prev.map(g => 
          g.id === gameId ? { ...g, wishlist: true, owned: false, try: false } : g
        ));
      } else {
        // Original toggle owned behavior for other cases
        await gameService.updateGameStatus(gameId, { 
          owned: !game.owned, 
          wishlist: false, 
          try: false 
        });
        
        // Optimistic update
        setGames(prev => prev.map(g => 
          g.id === gameId ? { ...g, owned: !g.owned, wishlist: false, try: false } : g
        ));
      }
      
      loadStats();
    } catch (error) {
      toast({
        title: "Error updating game",
        description: "Failed to update game status",
        variant: "destructive",
      });
    }
  }, [games, toast]);

  const handleToggleTry = useCallback(async (gameId) => {
    try {
      const game = games.find(g => g.id === gameId);
      
      // New logic: if game is "wishlist", clicking want to try icon changes it to want to try
      if (game.wishlist) {
        await gameService.updateGameStatus(gameId, { 
          try: true, 
          owned: false, 
          wishlist: false 
        });
        
        // Optimistic update
        setGames(prev => prev.map(g => 
          g.id === gameId ? { ...g, try: true, owned: false, wishlist: false } : g
        ));
      } else {
        // Original toggle try behavior for other cases
        await gameService.updateGameStatus(gameId, { 
          try: !game.try, 
          owned: false, 
          wishlist: false 
        });
        
        // Optimistic update
        setGames(prev => prev.map(g => 
          g.id === gameId ? { ...g, try: !g.try, owned: false, wishlist: false } : g
        ));
      }
      
      loadStats();
    } catch (error) {
      toast({
        title: "Error updating game",
        description: "Failed to update game status",
        variant: "destructive",
      });
    }
  }, [games, toast]);

  // Extract BGG ID from various URL formats
  const extractBGGId = (input) => {
    if (!input) return null;
    
    const trimmed = input.trim();
    
    // Check if it's just a number (game ID)
    if (/^\d+$/.test(trimmed)) {
      return trimmed;
    }
    
    // Extract ID from BGG URLs
    const patterns = [
      /boardgamegeek\.com\/boardgame\/(\d+)/i,
      /bgg\.cc\/(\d+)/i,
      /\/boardgame\/(\d+)/i
    ];
    
    for (const pattern of patterns) {
      const match = trimmed.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  };

  // Fetch BGG data and populate form
  const handleFetchBGGData = async () => {
    // Check authentication before allowing BGG fetch
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please enter PIN to access BoardGameGeek data fetching",
        variant: "destructive",
      });
      return;
    }

    if (!bggUrl.trim()) {
      toast({
        title: "BGG URL Required",
        description: "Please enter a BoardGameGeek URL or game ID",
        variant: "destructive",
      });
      return;
    }

    const bggId = extractBGGId(bggUrl);
    if (!bggId) {
      toast({
        title: "Invalid BGG URL",
        description: "Please enter a valid BoardGameGeek URL or game ID",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingBGG(true);
    try {
      console.log('Fetching BGG data for ID:', bggId);
      
      const bggData = await bggService.getGameDetails(bggId);
      
      if (!bggData) {
        toast({
          title: "BGG Data Not Found",
          description: "Could not find game data on BoardGameGeek. Please check the ID/URL and try again.",
          variant: "destructive",
        });
        return;
      }

      console.log('BGG data received:', bggData);
      
      // Update form with BGG data while preserving existing status
      setNewGame(prev => ({
        ...prev,
        // Update core game info from BGG
        name: bggData.name || prev.name,
        description: bggData.description || prev.description,
        image: bggData.image || prev.image,
        yearPublished: bggData.yearPublished || prev.yearPublished,
        minPlayers: bggData.minPlayers || prev.minPlayers,
        maxPlayers: bggData.maxPlayers || prev.maxPlayers,
        playtime: bggData.playtime || prev.playtime,
        complexity: bggData.complexity || prev.complexity,
        tags: bggData.categories ? bggData.categories.join(', ') : prev.tags,
        bggId: bggId, // Set the extracted BGG ID
      }));

      toast({
        title: "BGG Data Loaded",
        description: `Successfully loaded data for "${bggData.name}"`,
        variant: "default",
      });

      // Clear the BGG URL input
      setBggUrl('');

    } catch (error) {
      console.error('Error fetching BGG data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch data from BoardGameGeek. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingBGG(false);
    }
  };

  const handleAddGame = async () => {
    try {
      const gameToAdd = {
        name: newGame.name,
        players: { min: parseInt(newGame.minPlayers), max: parseInt(newGame.maxPlayers) },
        playtime: parseInt(newGame.playtime),
        complexity: parseFloat(newGame.complexity),
        isCoOp: newGame.isCoOp,
        tags: newGame.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        description: newGame.description,
        owned: newGame.owned,
        wishlist: newGame.wishlist,
        try: newGame.try,
        weight: 1,
        image: newGame.image,
        bggId: newGame.bggId, // Include BGG ID if present
        yearPublished: newGame.yearPublished // Include year published if present
      };

      const createdGame = await gameService.addGame(gameToAdd);
      
      setShowAddGame(false);
      setNewGame({
        name: '', minPlayers: 1, maxPlayers: 4, playtime: 30, complexity: 1,
        isCoOp: false, tags: '', description: '', owned: true, wishlist: false,
        try: false, weight: 2, favorite: false, image: 'placeholder-dice-logo',
        rating: null, notes: '', bggId: ''
      });
      setBggUrl('');
      setIsLoadingBGG(false);

      toast({
        title: "Game added!",
        description: `${createdGame.name} has been added to your collection.`,
      });
      
      // Reload games and stats
      loadGames();
      loadStats();
    } catch (error) {
      toast({
        title: "Error adding game",
        description: "Failed to add game to collection",
        variant: "destructive",
      });
    }
  };

  const handleEnrichWithBGG = async () => {
    if (games.length === 0) {
      toast({
        title: "No games to enrich",
        description: "Add some games first, then enrich them with BGG data",
        variant: "destructive",
      });
      return;
    }

    // Filter games that need enrichment (skip those already enriched OR with manually-set BGG IDs)
    const gamesToEnrich = games.filter(game => 
      !((game.bggEnriched && game.bggId) || (game.bggId && game.bggId.trim() !== ''))
    );
    const skippedCount = games.length - gamesToEnrich.length;

    if (gamesToEnrich.length === 0) {
      toast({
        title: "No games need enrichment",
        description: "All games already have BGG content or manually-set BGG IDs. Use individual game editing to update specific games.",
      });
      return;
    }

    if (skippedCount > 0) {
      toast({
        title: "BGG Enrichment Starting",
        description: `Enriching ${gamesToEnrich.length} games (${skippedCount} already have BGG content)`,
      });
    }

    setIsEnrichingBGG(true);
    setBggProgress({ current: 0, total: gamesToEnrich.length, gameName: '' });

    try {
      const enrichedGames = await bggService.enrichGamesWithBGG(
        games, 
        (current, total, gameName) => {
          setBggProgress({ current, total, gameName });
        }
      );

      // Update games in backend with all BGG data
      let updatedCount = 0;
      for (const game of enrichedGames) {
        try {
          if (game.bggEnriched) {
            await gameService.updateGame(game.id, {
              // BGG enriched data
              image: game.image,
              description: game.description,
              tags: game.tags,
              complexity: game.complexity,
              players: game.players,
              playtime: game.playtime,
              rating: game.rating,
              bggId: game.bggId,
              yearPublished: game.yearPublished,
              bggEnriched: game.bggEnriched,
              bggEnrichedAt: game.bggEnrichedAt,
              
              // PRESERVE USER SETTINGS - Explicitly maintain user's game settings
              owned: game.owned,
              wishlist: game.wishlist,
              try: game.try,
              isCoOp: game.isCoOp // Preserve co-op checkbox
            });
            updatedCount++;
          }
        } catch (error) {
          console.error(`Failed to update ${game.name}:`, error);
        }
      }
      
      console.log(`Updated ${updatedCount} games in backend`)

      await loadGames(); // Reload to show updated data
      
      const enrichedCount = enrichedGames.filter(g => g.bggEnriched).length;
      const totalProcessed = gamesToEnrich.length;
      const totalSkipped = skippedCount;
      
      toast({
        title: "BGG Enrichment Complete! 🎲",
        description: `Enriched ${enrichedCount}/${totalProcessed} games. ${totalSkipped} games with existing BGG content or manual BGG IDs were preserved.`,
      });
    } catch (error) {
      toast({
        title: "BGG Enrichment Failed",
        description: "Some games may not have been updated. Check console for details.",
        variant: "destructive",
      });
    } finally {
      setIsEnrichingBGG(false);
      setBggProgress({ current: 0, total: 0, gameName: '' });
    }
  };

  const handleSelectRandomGame = (game) => {
    toast({
      title: "Great choice!",
      description: `Enjoy playing ${game.name}!`,
    });
  };

  // Add loading indicator for main content
  if (isLoading && games.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🎲</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading games...</h2>
          <p className="text-gray-600">Please wait while we fetch your collection</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm transition-colors">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Responsive header - stacked on mobile, single row on desktop */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            {/* Logo and title */}
            <div className="flex items-center justify-center md:justify-start mb-4 md:mb-0">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white text-center md:text-left">
                🎲 Zaibatsui Game Manager
              </h1>
            </div>
            
            {/* Mobile button row: view modes on left, actions on right */}
            <div className="flex md:hidden items-center justify-between mb-4">
              {/* Left side: View mode buttons */}
              <div className="flex items-center gap-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className={`${viewMode === 'grid' 
                    ? 'bg-gray-900 dark:bg-gray-900 text-white dark:text-white' 
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  } px-2`}
                >
                  <Grid3X3 size={14} />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={`${viewMode === 'list' 
                    ? 'bg-gray-900 dark:bg-gray-900 text-white dark:text-white' 
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  } px-2`}
                >
                  <List size={14} />
                </Button>
              </div>

              {/* Right side: Add Game and Settings buttons */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => requireAuthentication(() => setShowAddGame(true))}
                  className="bg-orange-600 dark:bg-orange-600 hover:bg-orange-700 dark:hover:bg-orange-700 text-white dark:text-white px-3"
                  size="sm"
                >
                  <Plus size={14} className="mr-1" />
                  Add Game
                </Button>

                <Button
                  variant="outline"
                  onClick={() => setShowSettings(true)}
                  className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 px-2"
                  size="sm"
                  title="Settings"
                >
                  <Settings size={14} />
                </Button>
              </div>
            </div>

            {/* Mobile Quick Filter Buttons - Only show on mobile */}
            <div className="flex md:hidden items-center gap-2 mb-4">
              <button 
                onClick={() => handleStatsFilter('owned')}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs border transition-all ${
                  filters.ownedOnly 
                    ? 'bg-emerald-100 dark:bg-emerald-800/50 border-emerald-300 dark:border-emerald-600 text-emerald-700 dark:text-emerald-300' 
                    : 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-800/40'
                }`}
              >
                <Trophy size={12} />
                <span className="font-medium">{stats.owned || 0} owned</span>
              </button>
              <button 
                onClick={() => handleStatsFilter('wishlist')}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs border transition-all ${
                  filters.wishlistOnly 
                    ? 'bg-blue-100 dark:bg-blue-800/50 border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300' 
                    : 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/40'
                }`}
              >
                <Bookmark size={12} />
                <span className="font-medium">{stats.wishlist || 0} wishlist</span>
              </button>
              <button 
                onClick={() => handleStatsFilter('try')}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs border transition-all ${
                  filters.tryOnly 
                    ? 'bg-amber-100 dark:bg-amber-800/50 border-amber-300 dark:border-amber-600 text-amber-700 dark:text-amber-300' 
                    : 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-800/40'
                }`}
              >
                <Target size={12} />
                <span className="font-medium">{stats.try || 0} to try</span>
              </button>
            </div>

            {/* Desktop stats and action buttons - hidden on mobile */}
            <div className="hidden md:flex items-center gap-3">
              {/* Quick Stats - Clickable Filters */}
              <div className="hidden lg:flex items-center gap-6 text-sm mr-6">
                <button 
                  onClick={() => handleStatsFilter('owned')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all hover:shadow-md ${
                    filters.ownedOnly 
                      ? 'bg-emerald-100 dark:bg-emerald-800/50 border-emerald-300 dark:border-emerald-600 shadow-sm' 
                      : 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700 hover:bg-emerald-100 dark:hover:bg-emerald-800/40'
                  }`}
                  title="Click to filter owned games"
                >
                  <Trophy size={16} className="text-emerald-600 dark:text-emerald-400" />
                  <span className="text-emerald-700 dark:text-emerald-300 font-medium">{stats.owned || 0} owned</span>
                </button>
                <button 
                  onClick={() => handleStatsFilter('wishlist')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all hover:shadow-md ${
                    filters.wishlistOnly 
                      ? 'bg-blue-100 dark:bg-blue-800/50 border-blue-300 dark:border-blue-600 shadow-sm' 
                      : 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-800/40'
                  }`}
                  title="Click to filter wishlist games"
                >
                  <Bookmark size={16} className="text-blue-600 dark:text-blue-400" />
                  <span className="text-blue-700 dark:text-blue-300 font-medium">{stats.wishlist || 0} wishlist</span>
                </button>
                <button 
                  onClick={() => handleStatsFilter('try')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all hover:shadow-md ${
                    filters.tryOnly 
                      ? 'bg-amber-100 dark:bg-amber-800/50 border-amber-300 dark:border-amber-600 shadow-sm' 
                      : 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-800/40'
                  }`}
                  title="Click to filter want to try games"
                >
                  <Target size={16} className="text-amber-600 dark:text-amber-400" />
                  <span className="text-amber-700 dark:text-amber-300 font-medium">{stats.try || 0} to try</span>
                </button>
              </div>

              {/* Add Game Button */}
              <Button 
                onClick={() => requireAuthentication(() => setShowAddGame(true))}
                className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm"
              >
                <Plus className="mr-2" size={16} />
                Add Game
              </Button>

              {/* Settings Button */}
              <Button
                variant="outline"
                onClick={() => setShowSettings(true)}
                className="border-gray-500 dark:border-gray-500 text-gray-300 dark:text-gray-300 hover:bg-gray-600 dark:hover:bg-gray-600 hover:text-white dark:hover:text-white"
                title="Settings"
              >
                <Settings size={16} />
              </Button>
            </div>
          </div>

          {/* Dialogs */}
          <Dialog open={showAddGame} onOpenChange={setShowAddGame}>
                <DialogContent className="max-w-md bg-gray-800 dark:bg-gray-800 border-gray-600 dark:border-gray-600">
                  <DialogHeader>
                    <DialogTitle className="text-white dark:text-white">Add New Game</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-300 dark:text-gray-300">Game Name</Label>
                      <Input
                        value={newGame.name}
                        onChange={(e) => setNewGame(prev => ({ ...prev, name: e.target.value }))}
                        className="bg-gray-700 dark:bg-gray-700 border-gray-600 dark:border-gray-600 text-white dark:text-white focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                        placeholder="Enter game name"
                      />
                    </div>
                    
                    {/* BGG Import Section - Only show if authenticated */}
                    {isAuthenticated ? (
                      <div className="border border-gray-600 dark:border-gray-600 rounded-lg p-3 bg-gray-750 dark:bg-gray-750">
                        <Label className="text-gray-300 dark:text-gray-300 text-sm font-medium">BoardGameGeek Import</Label>
                        <p className="text-xs text-gray-400 dark:text-gray-400 mt-1">
                          Enter a BoardGameGeek URL or game ID to auto-populate game details
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Input
                            value={bggUrl}
                            onChange={(e) => setBggUrl(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && !isLoadingBGG && bggUrl.trim() && handleFetchBGGDataForNewGame()}
                            className="bg-gray-700 dark:bg-gray-700 border-gray-600 dark:border-gray-600 text-white dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                            placeholder="https://boardgamegeek.com/boardgame/3955/bang or just 3955"
                            disabled={isLoadingBGG}
                          />
                          <Button
                            onClick={handleFetchBGGDataForNewGame}
                            disabled={isLoadingBGG || !bggUrl.trim()}
                            variant="outline"
                            size="sm"
                            className="border-orange-500 dark:border-orange-500 text-orange-400 dark:text-orange-400 hover:bg-orange-600 dark:hover:bg-orange-600 hover:text-white dark:hover:text-white whitespace-nowrap px-3"
                          >
                            {isLoadingBGG ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-2 border-current border-t-transparent mr-2" />
                                Loading
                              </>
                            ) : (
                              <>
                                <Download size={14} className="mr-2" />
                                Fetch Data
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="border border-gray-600 dark:border-gray-600 rounded-lg p-3 bg-gray-750 dark:bg-gray-750 opacity-60">
                        <Label className="text-gray-500 dark:text-gray-500 text-sm font-medium">BoardGameGeek Import</Label>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          🔒 PIN authentication required to access BoardGameGeek data fetching
                        </p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-gray-300 dark:text-gray-300">Min Players</Label>
                        <Input
                          type="number"
                          min="1"
                          value={newGame.minPlayers}
                          onChange={(e) => setNewGame(prev => ({ ...prev, minPlayers: e.target.value }))}
                          className="bg-gray-700 dark:bg-gray-700 border-gray-600 dark:border-gray-600 text-white dark:text-white focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300 dark:text-gray-300">Max Players</Label>
                        <Input
                          type="number"
                          min="1"
                          value={newGame.maxPlayers}
                          onChange={(e) => setNewGame(prev => ({ ...prev, maxPlayers: e.target.value }))}
                          className="bg-gray-700 dark:bg-gray-700 border-gray-600 dark:border-gray-600 text-white dark:text-white focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-gray-300 dark:text-gray-300">Playtime (minutes)</Label>
                        <Input
                          type="number"
                          min="1"
                          value={newGame.playtime}
                          onChange={(e) => setNewGame(prev => ({ ...prev, playtime: e.target.value }))}
                          className="bg-gray-700 dark:bg-gray-700 border-gray-600 dark:border-gray-600 text-white dark:text-white focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300 dark:text-gray-300">Complexity (1-5)</Label>
                        <Input
                          type="number"
                          min="1"
                          max="5"
                          step="0.1"
                          value={newGame.complexity}
                          onChange={(e) => setNewGame(prev => ({ ...prev, complexity: e.target.value }))}
                          className="bg-gray-700 dark:bg-gray-700 border-gray-600 dark:border-gray-600 text-white dark:text-white focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-gray-300 dark:text-gray-300">Tags (comma separated)</Label>
                      <Input
                        value={newGame.tags}
                        onChange={(e) => setNewGame(prev => ({ ...prev, tags: e.target.value }))}
                        className="bg-gray-700 dark:bg-gray-700 border-gray-600 dark:border-gray-600 text-white dark:text-white focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                        placeholder="strategy, family, card game"
                      />
                    </div>

                    <div>
                      <Label className="text-gray-300 dark:text-gray-300">Description</Label>
                      <Textarea
                        value={newGame.description}
                        onChange={(e) => setNewGame(prev => ({ ...prev, description: e.target.value }))}
                        className="bg-gray-700 dark:bg-gray-700 border-gray-600 dark:border-gray-600 text-white dark:text-white focus:border-orange-400 dark:focus:border-orange-400 focus:ring-orange-900/20 dark:focus:ring-orange-900/20"
                        placeholder="Brief description of the game"
                      />
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center justify-center sm:justify-start gap-2">
                          <Checkbox
                            id="coop"
                            checked={newGame.isCoOp}
                            onCheckedChange={(checked) => setNewGame(prev => ({ ...prev, isCoOp: checked }))}
                            className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                          />
                          <Label htmlFor="coop" className="text-gray-300 dark:text-gray-300">Cooperative</Label>
                        </div>
                        
                        <div className="flex items-center justify-center sm:justify-start gap-2">
                          <Checkbox
                            id="owned"
                            checked={newGame.owned}
                            onCheckedChange={(checked) => {
                              setNewGame(prev => ({ 
                                ...prev, 
                                owned: checked,
                                wishlist: checked ? false : prev.wishlist,
                                try: checked ? false : prev.try
                              }));
                            }}
                            className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                          />
                          <Label htmlFor="owned" className="text-gray-300 dark:text-gray-300">Owned</Label>
                        </div>

                        <div className="flex items-center justify-center sm:justify-start gap-2">
                          <Checkbox
                            id="wishlist"
                            checked={newGame.wishlist}
                            onCheckedChange={(checked) => {
                              setNewGame(prev => ({ 
                                ...prev, 
                                wishlist: checked,
                                owned: checked ? false : prev.owned,
                                try: checked ? false : prev.try
                              }));
                            }}
                            className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                          />
                          <Label htmlFor="wishlist" className="text-gray-300 dark:text-gray-300">Add to Wishlist</Label>
                        </div>

                        <div className="flex items-center justify-center sm:justify-start gap-2">
                          <Checkbox
                            id="try"
                            checked={newGame.try}
                            onCheckedChange={(checked) => {
                              setNewGame(prev => ({ 
                                ...prev, 
                                try: checked,
                                owned: checked ? false : prev.owned,
                                wishlist: checked ? false : prev.wishlist
                              }));
                            }}
                            className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                          />
                          <Label htmlFor="try" className="text-gray-300 dark:text-gray-300">Want to Try</Label>
                        </div>
                      </div>
                    </div>

                    <Button 
                      onClick={handleAddGame}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                      disabled={!newGame.name.trim()}
                    >
                      Add Game
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

          {/* BGG Progress Indicator */}
          {isEnrichingBGG && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg px-4 py-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center justify-center sm:justify-start gap-2">
                  <RefreshCw className="animate-spin text-blue-600 dark:text-blue-400" size={16} />
                  <span className="text-blue-800 dark:text-blue-300 font-medium">
                    Enriching games with BoardGameGeek data...
                  </span>
                </div>
                <span className="text-blue-600 dark:text-blue-400 text-sm">
                  {bggProgress.current}/{bggProgress.total}
                </span>
              </div>
              <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2 mb-2">
                <div 
                  className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${(bggProgress.current / bggProgress.total) * 100}%` }}
                />
              </div>
              {bggProgress.gameName && (
                <div className="text-blue-700 dark:text-blue-300 text-sm">
                  Processing: {bggProgress.gameName}
                </div>
              )}
            </div>
          )}

          {/* Search and view controls */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <div className="relative flex-1 max-w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-orange-300 dark:focus:border-orange-600 focus:ring-orange-100 dark:focus:ring-orange-900/20 shadow-sm"
                placeholder="Search games and tags..."
              />
            </div>
            
            {/* Mobile Clear Filters Button - Only visible on mobile */}
            <div className="md:hidden">
              <Button
                variant="outline"
                onClick={handleClearFilters}
                className="w-full border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:border-orange-300 dark:hover:border-orange-600 hover:text-orange-600 dark:hover:text-orange-400"
              >
                <Trash2 size={16} className="mr-2" />
                Clear Filters
              </Button>
            </div>
            
            {/* View mode buttons - hidden on mobile (now in header) */}
            <div className="hidden md:flex items-center justify-center sm:justify-start gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="border-gray-200 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-600"
              >
                <Grid3X3 size={16} />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="border-gray-200 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-600"
              >
                <List size={16} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            {/* Mobile filter toggle button */}
            <div className="lg:hidden mb-4">
              <Button
                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                variant="outline"
                className="w-full justify-between border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <span className="flex items-center gap-2">
                  <BookOpen size={16} />
                  Filters & Options
                </span>
                <div className={`transition-transform duration-200 ${isFiltersOpen ? 'rotate-180' : ''}`}>
                  ▼
                </div>
              </Button>
            </div>
            
            {/* Filter panel - always visible on desktop, collapsible on mobile */}
            <div className={`lg:block ${isFiltersOpen ? 'block' : 'hidden'}`}>
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onResetHistory={handleResetHistory}
                onPickRandom={handlePickRandom}
                matchCount={filteredGames.length}
                isRandomizing={isRandomizing}
              />
            </div>
          </div>

          {/* Games Grid */}
          <div className="lg:col-span-3">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Games {filteredGames.length > 0 && (
                  <span className="text-gray-500 dark:text-gray-400 text-sm font-normal">
                    ({filteredGames.length} games)
                  </span>
                )}
              </h2>
              
              {filteredGames.length > 0 && (
                <Button
                  onClick={handlePickRandom}
                  disabled={isRandomizing}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  <Dice6 className={isRandomizing ? 'animate-spin mr-2' : 'mr-2'} size={16} />
                  {isRandomizing ? 'Picking...' : '🎲 Random Pick'}
                </Button>
              )}
            </div>

            {filteredGames.length === 0 ? (
              <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <div className="text-6xl mb-4">🎯</div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No games found</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-center mb-4">
                    Try adjusting your filters or search terms to find games.
                  </p>
                  <Button onClick={handleClearFilters} variant="outline" className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:border-orange-300 dark:hover:border-orange-600 hover:text-orange-600 dark:hover:text-orange-400">
                    Clear all filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className={
                viewMode === 'grid' 
                  ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'
                  : 'space-y-3'
              }>
                {paginatedGames.map(game => (
                  viewMode === 'grid' ? (
                    <GameCard
                      key={game.id}
                      game={game}
                      onEdit={handleGameEdit}
                      onToggleOwned={handleToggleOwned}
                      onToggleTry={handleToggleTry}
                      onPlay={handlePlayGame}
                    />
                  ) : (
                    <GameListItem
                      key={game.id}
                      game={game}
                      onEdit={handleGameEdit}
                      onToggleOwned={handleToggleOwned}
                      onToggleTry={handleToggleTry}
                      onPlay={handlePlayGame}
                      onGameClick={handleGameClick}
                    />
                  )
                ))}
              </div>
            )}
            
            {/* Pagination Controls */}
            {filteredGames.length > gamesPerPage && (
              <div className="mt-6">
                {/* Mobile Pagination - Compact */}
                <div className="md:hidden flex flex-col items-center gap-3">
                  <div className="flex items-center gap-2 w-full">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="flex-1 min-h-[44px] border-gray-300 dark:border-gray-600 text-sm"
                    >
                      ← Prev
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {(() => {
                        const maxButtons = 3; // Show only 3 page numbers on mobile
                        const buttons = [];
                        
                        if (totalPages <= maxButtons) {
                          // Show all pages if total is small
                          for (let i = 1; i <= totalPages; i++) {
                            buttons.push(
                              <Button
                                key={i}
                                variant={currentPage === i ? "default" : "outline"}
                                onClick={() => setCurrentPage(i)}
                                className={`min-h-[44px] min-w-[44px] px-2 ${
                                  currentPage === i 
                                    ? "bg-orange-500 hover:bg-orange-600 text-white" 
                                    : "border-gray-300 dark:border-gray-600"
                                }`}
                              >
                                {i}
                              </Button>
                            );
                          }
                        } else {
                          // Show current page with one before and one after
                          let startPage = Math.max(1, currentPage - 1);
                          let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                          
                          // Adjust if we're near the end
                          if (endPage === totalPages) {
                            startPage = Math.max(1, endPage - maxButtons + 1);
                          }
                          
                          if (startPage > 1) {
                            buttons.push(
                              <span key="ellipsis-start" className="text-gray-400 px-1">...</span>
                            );
                          }
                          
                          for (let i = startPage; i <= endPage; i++) {
                            buttons.push(
                              <Button
                                key={i}
                                variant={currentPage === i ? "default" : "outline"}
                                onClick={() => setCurrentPage(i)}
                                className={`min-h-[44px] min-w-[44px] px-2 ${
                                  currentPage === i 
                                    ? "bg-orange-500 hover:bg-orange-600 text-white" 
                                    : "border-gray-300 dark:border-gray-600"
                                }`}
                              >
                                {i}
                              </Button>
                            );
                          }
                          
                          if (endPage < totalPages) {
                            buttons.push(
                              <span key="ellipsis-end" className="text-gray-400 px-1">...</span>
                            );
                          }
                        }
                        
                        return buttons;
                      })()}
                    </div>
                    
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="flex-1 min-h-[44px] border-gray-300 dark:border-gray-600 text-sm"
                    >
                      Next →
                    </Button>
                  </div>
                  
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Page {currentPage} of {totalPages} • {filteredGames.length} games
                  </span>
                </div>
                
                {/* Desktop Pagination - Full */}
                <div className="hidden md:flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="border-gray-300 dark:border-gray-600"
                  >
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-2">
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          onClick={() => setCurrentPage(pageNum)}
                          className={currentPage === pageNum 
                            ? "bg-orange-500 hover:bg-orange-600 text-white" 
                            : "border-gray-300 dark:border-gray-600"
                          }
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="border-gray-300 dark:border-gray-600"
                  >
                    Next
                  </Button>
                  
                  <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                    Page {currentPage} of {totalPages} ({filteredGames.length} total games)
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Random Picker Modal */}
      <RandomPicker
        isOpen={showRandomPicker}
        onClose={() => setShowRandomPicker(false)}
        games={filteredGames}
        onSelectGame={handleSelectRandomGame}
      />

      {/* Game Edit Modal - No longer lazy loaded for speed */}
      <GameEditModal
        game={editingGame}
        isOpen={showEditGame}
        onClose={() => {
          setShowEditGame(false);
          setEditingGame(null);
        }}
        onSave={handleSaveGame}
        onDelete={handleDeleteGame}
        isAuthenticated={isAuthenticated}
      />

      <Suspense fallback={<div />}>
        <SettingsModal
          isOpen={showSettings}
          onClose={() => {
            setShowSettings(false);
            // Re-check authentication when settings closes
            checkAuthentication();
          }}
          onAuthChange={handleAuthChange}
          games={games}
          onBGGEnrich={handleEnrichWithBGG}
          isEnrichingBGG={isEnrichingBGG}
          bggProgress={bggProgress}
        />
      </Suspense>

      {/* Game Detail Modal */}
      <Dialog open={showGameDetail} onOpenChange={setShowGameDetail}>
        <DialogContent className="max-w-2xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white text-xl font-bold">
              {selectedGame?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedGame && (
            <div className="space-y-6">
              {/* Game Image */}
              <div className="flex justify-center">
                {selectedGame.image && selectedGame.image !== 'placeholder-dice-logo' ? (
                  <img
                    src={selectedGame.image.startsWith('data:') ? selectedGame.image : selectedGame.image}
                    alt={selectedGame.name}
                    className="w-48 h-48 object-cover rounded-lg shadow-lg"
                  />
                ) : (
                  <div className="w-48 h-48 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg shadow-lg flex items-center justify-center">
                    <div className="w-32 h-32 bg-white rounded-lg flex items-center justify-center">
                      <div className="text-4xl font-bold text-orange-600 transform rotate-12">Z</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Game Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedGame.players?.min === selectedGame.players?.max 
                      ? selectedGame.players?.min 
                      : `${selectedGame.players?.min}-${selectedGame.players?.max}`}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Players</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{selectedGame.playtime}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Minutes</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{selectedGame.complexity}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Complexity</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{selectedGame.playCount || 0}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Plays</div>
                </div>
              </div>

              {/* Status & Rating */}
              <div className="flex flex-wrap items-center justify-center gap-4">
                {selectedGame.owned && (
                  <Badge className="bg-emerald-100 dark:bg-emerald-800/50 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-600">
                    <Trophy size={14} className="mr-1" />
                    Owned
                  </Badge>
                )}
                {selectedGame.wishlist && (
                  <Badge className="bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-600">
                    <Bookmark size={14} className="mr-1" />
                    Wishlist
                  </Badge>
                )}
                {selectedGame.try && (
                  <Badge className="bg-amber-100 dark:bg-amber-800/50 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-600">
                    <Target size={14} className="mr-1" />
                    Want to Try
                  </Badge>
                )}
                {selectedGame.isCoOp && (
                  <Badge className="bg-purple-100 dark:bg-purple-800/50 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-600">
                    <Heart size={14} className="mr-1" />
                    Co-op
                  </Badge>
                )}
                {selectedGame.rating && (
                  <div className="flex items-center gap-1 px-3 py-1 bg-yellow-100 dark:bg-yellow-800/50 text-yellow-700 dark:text-yellow-300 rounded-full border border-yellow-200 dark:border-yellow-600">
                    <Star size={14} fill="currentColor" />
                    <span className="font-medium">{selectedGame.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>

              {/* Tags */}
              {selectedGame.tags && selectedGame.tags.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedGame.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedGame.description && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">Description</h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    {selectedGame.description}
                  </p>
                </div>
              )}

              {/* Notes */}
              {selectedGame.notes && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">Notes</h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    {selectedGame.notes}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  onClick={() => {
                    setShowGameDetail(false);
                    handleGameEdit(selectedGame);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  <Edit3 size={14} className="mr-2" />
                  Edit Game
                </Button>
                <Button
                  onClick={() => setShowGameDetail(false)}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Toaster />
      
      {/* Footer with version */}
      <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 mt-8">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span>Zaibatsui Game Manager</span>
          <span>•</span>
          <span>v1.5.0</span>
        </div>
      </div>
    </div>
  );
};

export default GamePicker;